import { FC, useState, MouseEvent, useEffect } from 'react';
import { Box } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import ConfirmationDialog from 'src/components/DialogBox/ConfirmationDialog/ConfirmationDialog';
import RejectGRNDialog from 'src/components/DialogBox/RejectGRNDialog/RejectGRNDialog';
import ViewGRNDialog from 'src/components/DialogBox/ViewGRNDialog/ViewGRNDialog';
import {
  apiUrls,
  routeURL,
  confirmMessages,
  grnDialogTitles,
  dataStorageKeywords,
} from 'src/utils/properties.utils';
import { calculateDialogHeightByItemCount } from 'src/utils/dialog.utils';
import StyledSnackbar from 'src/components/StyledComponents/StyledSnackBar';
import { GRNStatus } from 'src/models/enums/GRNStatus.enum';
import { getApiErrorMessage } from 'src/utils/errorHandler';
import { axiosTokenInstance } from 'src/utils/axiosInstance.utils';
import {
  formatNumberWithCommas,
  handleNumberFieldChange,
} from 'src/utils/format.utils';
import { grnInitialValues } from 'src/utils/initialValues.utils';
import { grnValidation } from 'src/utils/validationScheme.utils';
import GRNDialog from 'src/components/DialogBox/GRNBox/GRNBox';
import TextField from 'src/components/InputFields/TextField';
import TextArea from 'src/components/InputFields/TextArea';
import RawMaterialSelect from 'src/components/InputFields/RawMaterialPicker';
import {
  ActionMenuButton,
  ActionMenuPopover,
  MenuItemsWrapper,
  MenuItemWithIcon,
} from 'src/components/StyledComponents/StyledActionMenu';
import {
  FlexRowStartBox,
  FlexColumnBox,
  BorderedSectionBox,
  SectionTitleTypography,
  ItemRowBox,
  ItemFieldBox,
  ItemFieldAlignedBox,
  ItemFieldAlignedBoxMl2,
  RemoveItemBox,
  TrashIcon,
} from 'src/components/DialogBox/POBox/POBox.styles';
import {
  ClickableContainer,
  AddItemIcon,
  AddItemText,
} from 'src/components/StyledComponents/StyledPageComponents';
import {
  GRNFormItem,
  GRNViewData,
  GRNItemUpdate,
  GRNDynamicFieldValues,
} from 'src/models/Management.interface';
import {
  createEmptyGRNItem,
  getDefaultGRNItems,
} from 'src/utils/initialValues.utils';
import VendorAutocomplete from 'src/components/InputFields/VendorAutocomplete';
import { useGetVendor } from 'src/services/vendor.service';
import { useGetBasicMaterials } from 'src/services/material.service';
import { getEncryptedLocalStorageData } from 'src/utils/dataStorageManagement.utils';
import {
  getGRNPermissions,
  GRNPermissions,
} from 'src/utils/grnPermissions.utils';
import { generateGRNPDF } from 'src/components/Invoice/GRNPDF';

interface RawMaterialInfo {
  name: string;
}

interface VendorInfo {
  organizationName: string;
}

interface GRNItemApiResponse {
  id: string;
  rawMaterialId: string;
  rawMaterialName?: string;
  rawMaterial?: RawMaterialInfo;
  receivedQty: number;
  returnedQty: number;
  unitPrice: number;
}

interface GRNApiResponse {
  id: string;
  vendorId?: string;
  vendorName?: string;
  vendor?: VendorInfo;
  createdAt: string;
  preparedBy?: string;
  invoiceNumber: string;
  invoiceAmount?: number;
  notes?: string;
  status: number;
  approvedDate?: string | null;
  approvedBy?: string | null;
  rejectedDate?: string | null;
  rejectedBy?: string | null;
  rejectedNotes?: string | null;
  totalPaidAmount?: number;
  items?: GRNItemApiResponse[];
}

const GRNActionMenuButton: FC<{
  id: string;
  status: GRNStatus;
  resetPageAndRefresh: () => void;
  triggerRefresh: () => void;
  deleteGRN: (id: string) => void;
}> = ({ id, status, resetPageAndRefresh, triggerRefresh, deleteGRN }) => {
  const navigate = useNavigate();
  const { showSnackbar } = StyledSnackbar();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewData, setViewData] = useState<GRNViewData | null>(null);
  const [loading, setLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [items, setItems] = useState<GRNFormItem[]>(getDefaultGRNItems());
  const [permissions, setPermissions] = useState<GRNPermissions>({
    canView: false,
    canEdit: false,
    canUpdateStatus: false,
    canDelete: false,
    canCreatePayment: false,
    canPrint: false,
  });
  const open = Boolean(anchorEl);
  const { basicVendors, fetchBasicVendors } = useGetVendor();
  const { basicMaterials, fetchBasicMaterials } = useGetBasicMaterials();

  useEffect(() => {
    const fetchRole = async () => {
      const role = await getEncryptedLocalStorageData(
        dataStorageKeywords.HIGHEST_ROLE
      );
      setPermissions(getGRNPermissions(role || '', status));
    };
    fetchRole();
  }, []);

  const hasAnyAction = () => {
    return (
      permissions.canView ||
      permissions.canEdit ||
      permissions.canUpdateStatus ||
      permissions.canDelete ||
      permissions.canCreatePayment ||
      permissions.canPrint
    );
  };
  const handleClose = () => setAnchorEl(null);

  const handleClick = (event: MouseEvent<HTMLElement>) =>
    setAnchorEl(event.currentTarget);

  const handleViewDialogOpen = async () => {
    handleClose();
    try {
      const response = await axiosTokenInstance.get(apiUrls.GET_GRN_BY_ID, {
        params: { id: id },
      });
      const grnData: GRNApiResponse = response.data.data;

      setViewData({
        id: grnData.id,
        vendorName:
          grnData.vendorName || grnData.vendor?.organizationName || '-',
        createdAt: grnData.createdAt,
        preparedBy: grnData.preparedBy || '-',
        invoiceNumber: grnData.invoiceNumber || '-',
        invoiceAmount: grnData.invoiceAmount || 0,
        notes: grnData.notes || '',
        status: Number(grnData.status),
        approvedDate: grnData.approvedDate ?? null,
        approvedBy: grnData.approvedBy ?? null,
        rejectedDate: grnData.rejectedDate ?? null,
        rejectedBy: grnData.rejectedBy ?? null,
        rejectedNotes: grnData.rejectedNotes ?? null,
        totalPaidAmount: grnData.totalPaidAmount,
        items:
          grnData.items?.map((item: GRNItemApiResponse) => ({
            id: item.id,
            rawMaterialName:
              item.rawMaterialName || item.rawMaterial?.name || '-',
            receivedQty: item.receivedQty || 0,
            returnedQty: item.returnedQty || 0,
            unitPrice: item.unitPrice || 0,
          })) || [],
      });
      setViewDialogOpen(true);
    } catch (error) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
    }
  };

  const handleViewDialogClose = () => {
    setViewDialogOpen(false);
    setViewData(null);
  };

  const handlePrint = async () => {
    handleClose();
    try {
      const response = await axiosTokenInstance.get(apiUrls.GET_GRN_BY_ID, {
        params: { id: id },
      });
      const grnData: GRNApiResponse = response.data.data;

      const grnViewData: GRNViewData = {
        id: grnData.id,
        vendorName:
          grnData.vendorName || grnData.vendor?.organizationName || '-',
        createdAt: grnData.createdAt,
        preparedBy: grnData.preparedBy || '-',
        invoiceNumber: grnData.invoiceNumber || '-',
        invoiceAmount: grnData.invoiceAmount || 0,
        notes: grnData.notes || '',
        status: Number(grnData.status),
        approvedDate: grnData.approvedDate ?? null,
        approvedBy: grnData.approvedBy ?? null,
        rejectedDate: grnData.rejectedDate ?? null,
        rejectedBy: grnData.rejectedBy ?? null,
        rejectedNotes: grnData.rejectedNotes ?? null,
        totalPaidAmount: grnData.totalPaidAmount,
        items:
          grnData.items?.map((item: GRNItemApiResponse) => ({
            id: item.id,
            rawMaterialName:
              item.rawMaterialName || item.rawMaterial?.name || '-',
            receivedQty: item.receivedQty || 0,
            returnedQty: item.returnedQty || 0,
            unitPrice: item.unitPrice || 0,
          })) || [],
      };

      await generateGRNPDF(grnViewData);
    } catch (error) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
    }
  };

  const handleCheckPayments = () => {
    navigate(routeURL.GRN_PAYMENTS_REDIRECT(id));
  };

  const handleDeleteDialogOpen = () => {
    setDeleteDialogOpen(true);
    handleClose();
  };

  const handleDeleteConfirm = async () => {
    try {
      if (id) {
        await deleteGRN(id);
      }
      resetPageAndRefresh();
    } catch (error) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
    } finally {
      setDeleteDialogOpen(false);
    }
  };

  const handleApproveDialogOpen = () => {
    setApproveDialogOpen(true);
    handleClose();
  };

  const handleApproveConfirm = async () => {
    try {
      const response = await axiosTokenInstance.patch(
        apiUrls.TOGGLE_GRN_STATUS,
        {
          id: id,
          status: GRNStatus.APPROVED,
        }
      );
      showSnackbar(response.data.message, 'success');
      triggerRefresh();
    } catch (error) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
    } finally {
      setApproveDialogOpen(false);
    }
  };

  const handleRejectDialogOpen = () => {
    setRejectDialogOpen(true);
    handleClose();
  };

  const handleRejectConfirm = async (reason: string) => {
    try {
      const response = await axiosTokenInstance.patch(
        apiUrls.TOGGLE_GRN_STATUS,
        {
          id: id,
          status: GRNStatus.REJECTED,
          rejectedNotes: reason,
        }
      );
      showSnackbar(response.data.message, 'success');
      triggerRefresh();
    } catch (error) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
    } finally {
      setRejectDialogOpen(false);
    }
  };

  const handleEditDialogOpen = async () => {
    handleClose();
    fetchBasicVendors();
    fetchBasicMaterials();
    try {
      const response = await axiosTokenInstance.get(apiUrls.GET_GRN_BY_ID, {
        params: { id: id },
      });
      const grnData: GRNApiResponse = response.data.data;
      const dynamicFieldValues: GRNDynamicFieldValues = {};
      const loadedItems: GRNFormItem[] =
        grnData.items?.map((item: GRNItemApiResponse) => {
          dynamicFieldValues[`rawMaterialId-${item.id}`] = item.rawMaterialId;
          dynamicFieldValues[`receivedQty-${item.id}`] =
            item.receivedQty?.toString() || '';
          dynamicFieldValues[`returnedQty-${item.id}`] =
            item.returnedQty?.toString() || '0';
          dynamicFieldValues[`unitPrice-${item.id}`] =
            item.unitPrice?.toString() || '';

          return {
            rawMaterialId: item.rawMaterialId,
            receivedQty: item.receivedQty?.toString() || '',
            returnedQty: item.returnedQty?.toString() || '0',
            unitPrice: item.unitPrice?.toString() || '',
            id: item.id,
            isNew: false,
          };
        }) || [];

      if (loadedItems.length === 0) {
        loadedItems.push(createEmptyGRNItem());
      }

      formik.setValues({
        id: grnData.id,
        vendor: grnData.vendorId || '',
        invoiceNumber: grnData.invoiceNumber || '',
        invoiceAmount: grnData.invoiceAmount?.toString() || '',
        notes: grnData.notes || '',
        status: grnData.status,
        grnItems: [],
        ...dynamicFieldValues,
      });

      setItems(loadedItems);
      setEditDialogOpen(true);
    } catch (error) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
    }
  };

  const handleEditDialogClose = () => {
    setEditDialogOpen(false);
    formik.resetForm();
    setItems(getDefaultGRNItems());
  };

  const validateFormItems = async () => {
    const values = formik.values as Record<string, any>;
    const grnItems: GRNFormItem[] = items.map((item) => {
      const rawMaterialId =
        values[`rawMaterialId-${item.id}`] || item.rawMaterialId;
      const receivedQty = values[`receivedQty-${item.id}`] || item.receivedQty;
      const returnedQty =
        values[`returnedQty-${item.id}`] || item.returnedQty || 0;
      const unitPrice = values[`unitPrice-${item.id}`] || item.unitPrice;

      const itemData: GRNFormItem = {
        rawMaterialId,
        receivedQty,
        returnedQty,
        unitPrice,
        id: item.id,
      };

      if (!item.isNew && item.id) {
        itemData.id = item.id;
      }

      return itemData;
    });
    setItems(grnItems);
    const errors = await formik.validateForm();
    if (Object.keys(errors).length) {
      const touched: Record<string, boolean> = {
        vendor: true,
        invoiceNumber: true,
        invoiceAmount: true,
      };
      items.forEach((item) => {
        touched[`rawMaterialId-${item.id}`] = true;
        touched[`receivedQty-${item.id}`] = true;
        touched[`returnedQty-${item.id}`] = true;
        touched[`unitPrice-${item.id}`] = true;
      });
      formik.setTouched(touched);
    }
    return Object.keys(errors).length === 0;
  };

  const handleEditSubmit = async (isCompleted: boolean) => {
    const isFormValid = await validateFormItems();
    if (!isFormValid) {
      return;
    }
    setLoading(true);
    const values = formik.values as Record<string, any>;
    const grnItems = items
      .map((item) => {
        const rawMaterialId =
          values[`rawMaterialId-${item.id}`] || item.rawMaterialId;
        const receivedQty =
          values[`receivedQty-${item.id}`] || item.receivedQty;
        const returnedQty =
          values[`returnedQty-${item.id}`] || item.returnedQty || 0;
        const unitPrice = values[`unitPrice-${item.id}`] || item.unitPrice;

        const itemData: GRNItemUpdate = {
          rawMaterialId,
          receivedQty: Number(receivedQty),
          returnedQty: Number(returnedQty),
          unitPrice: Number(unitPrice),
        };

        if (!item.isNew && item.id) {
          itemData.id = item.id;
        }

        return itemData;
      })
      .filter((item) => item.rawMaterialId && Number(item.receivedQty) > 0);

    try {
      const response = await axiosTokenInstance.put(apiUrls.EDIT_GRN, {
        id: id,
        vendorId: formik.values.vendor,
        invoiceNumber: formik.values.invoiceNumber,
        invoiceAmount: parseFloat(formik.values.invoiceAmount),
        notes: formik.values.notes || null,
        items: grnItems,
        isCompleted: isCompleted,
      });
      showSnackbar(response.data.message, 'success');
      triggerRefresh();
      handleEditDialogClose();
    } catch (error) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleAddItem = () => {
    setItems([...items, createEmptyGRNItem()]);
  };

  const handleRemoveItem = (itemId: string) => {
    if (items.length <= 1) return;
    setItems((prevItems) => prevItems.filter((item) => item.id !== itemId));
  };

  const formik = useFormik({
    initialValues: grnInitialValues,
    validationSchema: grnValidation(items),
    onSubmit: () => {
      // Form submission handled by individual action handlers
    },
  });

  const selectedMaterialIds = items
    .map(
      (item) =>
        (formik.values as Record<string, any>)[`rawMaterialId-${item.id}`] ||
        item.rawMaterialId
    )
    .filter(Boolean);

  return (
    <>
      {hasAnyAction() && (
        <ActionMenuButton
          onClick={(event: MouseEvent<HTMLElement>) => handleClick(event)}
          component="i"
          className="bx bx-dots-horizontal-rounded"
        />
      )}
      <ActionMenuPopover open={open} anchorEl={anchorEl} onClose={handleClose}>
        <MenuItemsWrapper>
          <MenuItemWithIcon
            onClick={handleViewDialogOpen}
            iconClassName="bx bx-file-find"
            text="View"
          />
          {permissions.canEdit && (
            <MenuItemWithIcon
              onClick={handleEditDialogOpen}
              iconClassName="bx bx-edit"
              text="Edit"
            />
          )}
          {permissions.canUpdateStatus && (
            <>
              <MenuItemWithIcon
                onClick={handleApproveDialogOpen}
                iconClassName="bx bx-check"
                text="Approve"
              />
              <MenuItemWithIcon
                onClick={handleRejectDialogOpen}
                iconClassName="bx bx-x"
                text="Reject"
              />
            </>
          )}
          {permissions.canCreatePayment && (
            <MenuItemWithIcon
              onClick={() => {
                handleClose();
                navigate(routeURL.GRN_PAYMENTS_REDIRECT(id));
              }}
              iconClassName="bx bx-money"
              text="Payments"
            />
          )}
          {permissions.canDelete && (
            <MenuItemWithIcon
              onClick={handleDeleteDialogOpen}
              iconClassName="bx bx-trash"
              text="Delete"
            />
          )}
          <MenuItemWithIcon
            onClick={handlePrint}
            iconClassName="bx bx-printer"
            text="Print"
          />
        </MenuItemsWrapper>
      </ActionMenuPopover>
      <ViewGRNDialog
        open={viewDialogOpen}
        onClose={handleViewDialogClose}
        data={viewData!}
        onCheckPayments={handleCheckPayments}
      />
      <ConfirmationDialog
        height="275px"
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        title={grnDialogTitles.DELETE}
        subtitle={confirmMessages.CONFIRM_DELETE_GRN}
        confirmText="Delete"
        confirmButtonColor="var(--error)"
        alertIconColor="var(--error)"
      />
      <ConfirmationDialog
        height="275px"
        open={approveDialogOpen}
        onClose={() => setApproveDialogOpen(false)}
        onConfirm={handleApproveConfirm}
        title={grnDialogTitles.APPROVE}
        subtitle={confirmMessages.CONFIRM_APPROVE_GRN}
        confirmText="Approve"
        confirmButtonColor="var(--success)"
        alertIconColor="var(--success)"
      />
      <RejectGRNDialog
        open={rejectDialogOpen}
        onClose={() => setRejectDialogOpen(false)}
        onConfirm={handleRejectConfirm}
      />
      <GRNDialog
        height={`${calculateDialogHeightByItemCount(items.length)}px`}
        open={editDialogOpen}
        handleClose={handleEditDialogClose}
        title={grnDialogTitles.EDIT}
        subtitle={confirmMessages.CONFIRM_EDIT_GRN}
        formTitle=""
        onSubmit={formik.handleSubmit}
        buttonDisabled={loading}
        buttonLabel="Save"
        buttonLabel2={status === GRNStatus.NEW ? 'Save and Update' : undefined}
        onSaveClick={() => handleEditSubmit(false)}
        onSaveAndCompleteClick={
          status === GRNStatus.NEW ? () => handleEditSubmit(true) : undefined
        }
      >
        <Box display="flex" flexDirection="column">
          <FlexRowStartBox>
            <FlexColumnBox>
              <VendorAutocomplete
                id="vendor"
                label="Vendor"
                formikProps={formik}
                options={basicVendors}
                width="238px"
              />
            </FlexColumnBox>
            <FlexColumnBox>
              <TextField
                color="var(--subtext)"
                width="238px"
                id="invoiceNumber"
                label="Invoice Number"
                formikProps={formik}
              />
            </FlexColumnBox>
            <FlexColumnBox>
              <TextField
                color="var(--subtext)"
                width="238px"
                id="invoiceAmount"
                label="Invoice Amount"
                formikProps={formik}
                type="text"
                value={formatNumberWithCommas(formik.values.invoiceAmount)}
                onChange={(e) =>
                  handleNumberFieldChange(e, formik, 'invoiceAmount')
                }
              />
            </FlexColumnBox>
          </FlexRowStartBox>
          <Box mb={3}>
            <TextArea
              color="var(--subtext)"
              id="notes"
              label="Notes"
              formikProps={formik}
              rows={3}
              height="100%"
              width="578px"
              optional={true}
              overflowY="hidden"
            />
          </Box>
          <Box>
            <SectionTitleTypography>Items</SectionTitleTypography>
            <BorderedSectionBox>
              {items.map((item, index) => (
                <ItemRowBox key={item.id}>
                  <ItemFieldBox>
                    <RawMaterialSelect
                      showLabel={index === 0}
                      color="var(--subtext)"
                      width="235px"
                      id={`rawMaterialId-${item.id}`}
                      label="Item"
                      formikProps={formik}
                      selectedIds={selectedMaterialIds}
                      rawMaterialsList={basicMaterials}
                    />
                  </ItemFieldBox>
                  <ItemFieldAlignedBox>
                    <TextField
                      showLabel={index === 0}
                      color="var(--subtext)"
                      width="160px"
                      id={`receivedQty-${item.id}`}
                      label="Received Quantity"
                      formikProps={formik}
                      type="text"
                      value={formatNumberWithCommas(
                        (formik.values as any)[`receivedQty-${item.id}`]
                      )}
                      onChange={(e) =>
                        handleNumberFieldChange(
                          e,
                          formik,
                          `receivedQty-${item.id}`
                        )
                      }
                    />
                  </ItemFieldAlignedBox>
                  <ItemFieldAlignedBoxMl2>
                    <TextField
                      showLabel={index === 0}
                      color="var(--subtext)"
                      width="160px"
                      id={`returnedQty-${item.id}`}
                      label="Returned Quantity"
                      formikProps={formik}
                      errorTooltiplength={15}
                      type="text"
                      value={formatNumberWithCommas(
                        (formik.values as any)[`returnedQty-${item.id}`]
                      )}
                      onChange={(e) =>
                        handleNumberFieldChange(
                          e,
                          formik,
                          `returnedQty-${item.id}`
                        )
                      }
                    />
                  </ItemFieldAlignedBoxMl2>
                  <ItemFieldAlignedBoxMl2>
                    <TextField
                      showLabel={index === 0}
                      color="var(--subtext)"
                      width="160px"
                      id={`unitPrice-${item.id}`}
                      label="Unit Price"
                      formikProps={formik}
                      type="text"
                      value={formatNumberWithCommas(
                        (formik.values as any)[`unitPrice-${item.id}`]
                      )}
                      onChange={(e) =>
                        handleNumberFieldChange(
                          e,
                          formik,
                          `unitPrice-${item.id}`
                        )
                      }
                    />
                  </ItemFieldAlignedBoxMl2>
                  {items.length > 1 && (
                    <RemoveItemBox>
                      <TrashIcon
                        component="i"
                        className="bx bx-trash"
                        onClick={() => handleRemoveItem(item.id)}
                      />
                    </RemoveItemBox>
                  )}
                </ItemRowBox>
              ))}
            </BorderedSectionBox>
          </Box>
          <ClickableContainer
            width="100px"
            display="flex"
            mt={2}
            mb={2}
            onClick={handleAddItem}
          >
            <AddItemIcon component="i" className="bx bx-plus" />
            <AddItemText>Add Item</AddItemText>
          </ClickableContainer>
        </Box>
      </GRNDialog>
    </>
  );
};

export default GRNActionMenuButton;
